import React, { useState } from 'react';
import { X, Check, Info, User, Mail, Phone, BedDouble } from 'lucide-react';
import { createReservation } from '../../services/api';
import { trackAddToCart } from '../../utils/analytics';
import { useLockFloatingButtons } from '../../hooks/useModalOpen';
import { validateReservationForm, focusFirstError } from '../../utils/formValidation';

const fmt = (n) => new Intl.NumberFormat('tr-TR').format(n || 0);

const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] focus:border-transparent';

/** Event-specific pre-reservation form; posts to the existing /api/reservations flow. */
const EventReservationModal = ({ event, onClose }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    participants: 1, roomType: 'double', notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  useLockFloatingButtons();
  const singleFee = event.singleRoomSupplement || 0;
  const nights = event.nights || 0;

  const fieldCls = (field, extra = '') =>
    `${inputCls} ${extra} ${fieldErrors[field] ? '!border-red-400 focus:!ring-red-300' : ''}`;

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (fieldErrors[k]) setFieldErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    const errors = validateReservationForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Lütfen işaretli alanları kontrol edin.');
      focusFirstError(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const participants = Number(form.participants) || 1;
      await createReservation({
        tourId: event.id,
        tourTitle: event.name,
        selectedDate: event.dateLabel,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        participants,
        roomType: form.roomType,
        notes: form.notes || '',
      });
      trackAddToCart({
        content_name: event.name,
        content_category: 'special-event',
        content_ids: [event.id],
        value: event.price * participants,
        currency: 'TRY',
        selected_date: event.dateLabel,
        num_items: participants,
        room_type: form.roomType,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Rezervasyon kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl max-w-md w-full p-8 text-center"
          onClick={(e) => e.stopPropagation()}
          data-testid="event-reservation-success"
        >
          <div className="w-16 h-16 bg-[#C8D843]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-[#3D4A26]" />
          </div>
          <h3 className="text-xl font-bold text-[#1B1B1B] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Ön rezervasyonunuz bize ulaştı
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            {event.dateLabel} tarihli özel etkinlik için yerinizi geçici olarak ayırdık.
            Rezervasyonunuzun kesinleşmesi için kişi başı <strong>4.000₺</strong> ön ödeme yapmanız
            gerekiyor
            {Number(form.participants) > 1 && (
              <> ({form.participants} kişi için toplam <strong>{fmt(Number(form.participants) * 4000)}₺</strong>)</>
            )}
            . Ödemeniz bize ulaştıktan sonra ekibimiz sizinle iletişime geçecek.
          </p>
          <div className="bg-[#f0f4d8] border border-[#C8D843]/40 rounded-xl p-4 text-left mb-6">
            <p className="text-xs font-bold text-[#3D4A26] uppercase tracking-wider mb-1.5">Ödeme bilgileri e-postanızda</p>
            <p className="text-xs text-[#3D4A26]/90 leading-relaxed break-words">
              Rezervasyon özetiniz ve banka hesap bilgilerimiz <strong>{form.email}</strong> adresine
              gönderildi. E-postayı göremezseniz lütfen spam veya gereksiz klasörünü kontrol edin.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="event-reservation-close-success"
            className="w-full bg-[#C8D843] text-[#1B1B1B] font-bold py-3 rounded-full hover:bg-[#b8c83a] transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start md:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-lg w-full my-6 max-h-[92dvh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="event-reservation-modal"
      >
        <div className="flex items-start justify-between px-6 pt-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#3D4A26]">Özel Etkinlik</p>
            <h3 className="text-lg font-bold text-[#1B1B1B] leading-tight mt-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {event.name}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{event.dateLabel} · {event.priceLabel} kişi başı</p>
          </div>
          <button onClick={onClose} aria-label="Kapat" data-testid="event-reservation-close" className="text-gray-400 hover:text-gray-700">
            <X size={22} />
          </button>
        </div>

        <div className="mx-6 mt-4 bg-[#f0f4d8] border border-[#C8D843]/30 rounded-xl p-4 flex items-start gap-3">
          <Info size={18} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#3D4A26]/90 leading-relaxed">
            Bu aşamada ödeme alınmaz. Formu gönderdikten sonra kontenjan durumunu ve ödeme adımlarını
            size iletiyoruz. Kontenjan 16 kişi ile sınırlı.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-6 space-y-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Adınız</label>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input required minLength={2} value={form.firstName} onChange={set('firstName')} data-field="firstName" data-testid="event-input-firstname" className={fieldCls('firstName', 'pl-9')} placeholder="Adınız" />
              </div>
              {fieldErrors.firstName && <p className="text-xs text-red-600 mt-1">{fieldErrors.firstName}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Soyadınız</label>
              <input required minLength={2} value={form.lastName} onChange={set('lastName')} data-field="lastName" data-testid="event-input-lastname" className={fieldCls('lastName')} placeholder="Soyadınız" />
              {fieldErrors.lastName && <p className="text-xs text-red-600 mt-1">{fieldErrors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-posta</label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input required type="email" value={form.email} onChange={set('email')} data-field="email" data-testid="event-input-email" className={fieldCls('email', 'pl-9')} placeholder="ornek@eposta.com" />
            </div>
            {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefon</label>
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input required minLength={6} inputMode="tel" value={form.phone} onChange={set('phone')} data-field="phone" data-testid="event-input-phone" className={fieldCls('phone', 'pl-9')} placeholder="05xx xxx xx xx" />
            </div>
            {fieldErrors.phone && <p className="text-xs text-red-600 mt-1">{fieldErrors.phone}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Kişi sayısı</label>
              <select value={form.participants} onChange={set('participants')} data-testid="event-input-participants" className={`${inputCls} bg-white`}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} kişi</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Oda tipi</label>
              <select value={form.roomType} onChange={set('roomType')} data-testid="event-input-roomtype" className={`${inputCls} bg-white`}>
                <option value="double">Çift Kişilik Oda</option>
                <option value="single">Tek Kişilik Oda{singleFee ? ` (+${fmt(singleFee)}₺/gece)` : ''}</option>
              </select>
            </div>
          </div>

          {form.roomType === 'single' && singleFee > 0 && (
            <div
              className="bg-[#f0f4d8] border border-[#C8D843]/40 rounded-xl p-3.5 flex items-start gap-2.5"
              data-testid="event-single-room-note"
            >
              <BedDouble size={16} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#3D4A26]/90 leading-relaxed">
                Tek kişilik odada gecelik <strong>{fmt(singleFee)}₺</strong> tek kişi farkı uygulanır.
                {nights > 0 && (
                  <> {nights} gece için toplam <strong>{fmt(singleFee * nights)}₺</strong> ek ücret oluşur.</>
                )}{' '}
                Bu fark kişi başı tur ücretine eklenir.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notunuz (opsiyonel)</label>
            <textarea rows={3} maxLength={1000} value={form.notes} onChange={set('notes')} data-testid="event-input-notes" className={`${inputCls} resize-none`} placeholder="Diyet, ulaşım veya oda tercihiniz" />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="event-reservation-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="event-reservation-submit"
            className="w-full bg-[#C8D843] text-[#1B1B1B] font-bold py-3.5 rounded-full text-base hover:bg-[#b8c83a] transition-all duration-300 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Gönderiliyor...' : 'Yerimi Ayır'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventReservationModal;
